import { useEffect, useState } from "react";

import {
  getCatalogPayers,
  getCatalogStates,
  type CatalogPayersInput,
  type CatalogStatesInput,
  type PayerCapability,
  type StateCapability,
} from "../api/catalog";

const cache = new Map<string, unknown>();
const inFlight = new Map<string, Promise<unknown>>();

const stableKey = (endpoint: string, input: object): string => {
  const entries = Object.entries(input).sort(([a], [b]) => a.localeCompare(b));
  const parts: string[] = [];
  for (const [key, value] of entries) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${key}=${String(value)}`);
  }
  return `${endpoint}?${parts.join("&")}`;
};

const fetchWithCache = <T>(
  key: string,
  fetcher: () => Promise<T>
): Promise<T> => {
  if (cache.has(key)) return Promise.resolve(cache.get(key) as T);
  const existing = inFlight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = fetcher()
    .then((result) => {
      cache.set(key, result);
      inFlight.delete(key);
      return result;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
};

export interface CatalogQueryState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

const useCatalogQuery = <T, I extends object>(
  endpoint: string,
  input: I,
  fetcher: (input: I) => Promise<T>,
  enabled: boolean
): CatalogQueryState<T> => {
  const key = stableKey(endpoint, input);
  const [state, setState] = useState<CatalogQueryState<T>>(() => {
    if (!enabled) return { data: null, loading: false, error: null };
    const cached = cache.get(key) as T | undefined;
    return cached !== undefined
      ? { data: cached, loading: false, error: null }
      : { data: null, loading: true, error: null };
  });

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ data: null, loading: false, error: null });
      return;
    }
    const cached = cache.get(key) as T | undefined;
    if (cached !== undefined) {
      setState({ data: cached, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithCache(key, () => fetcher(input))
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ data: null, loading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
};

export const useCatalogPayers = (
  input: CatalogPayersInput | null,
  enabled = true
): CatalogQueryState<PayerCapability[]> => {
  const effectiveEnabled = enabled && Boolean(input?.destinationCountryCode);
  return useCatalogQuery(
    "payers",
    input ?? ({} as CatalogPayersInput),
    getCatalogPayers,
    effectiveEnabled
  );
};

export const useCatalogStates = (
  countryCode: string | null | undefined,
  input: CatalogStatesInput = {},
  enabled = true
): CatalogQueryState<StateCapability[]> => {
  const effectiveEnabled = enabled && Boolean(countryCode);
  return useCatalogQuery(
    `countries/${countryCode ?? ""}/states`,
    input,
    (q) => getCatalogStates(countryCode as string, q),
    effectiveEnabled
  );
};
